import { html, LitElement, css } from 'lit';
import '@digital-realty/ix-grid/ix-grid.js';
import {
  cookieColumns,
  cookieStrictlyNecessary,
  cookiePerformance,
  cookieTargeting,
} from '../data/policy.js';
import { TWStyles } from '../styles/tw.js';
import { logo } from '../assets/logo.js';

export class IxCookiePolicy extends LitElement {
  static styles = [
    TWStyles,
    css`
      :host {
        background: rgba(245, 247, 255, 1);
      }
      section {
        margin-bottom: 2rem;
      }
      .dlr-section-title {
        font-size: 1.5rem;
        font-weight: 700;
        line-height: 2rem;
        font-family: var(--root-secondary-font);
      }
      p {
        margin-top: 0.5rem;
      }
      p a {
        color: rgba(20, 86, 224, 1);
        text-decoration: underline;
      }
      ix-grid {
        --ix-grid-hover-background: transparent;
        --ix-grid-cell-pointer: default;
      }
    `,
  ];

  // eslint-disable-next-line class-methods-use-this
  renderTable(rows: any) {
    return html`<ix-grid
      session-storage-key="cookie-policy"
      .columns=${cookieColumns}
      .rows=${rows}
      hide-filters
      hideHeader
      rowLimit=${rows.length}
    ></ix-grid>`;
  }

  render() {
    return html`
      <div class="py-8 px-4 md:px-16">
        <section>${logo}</section>
        <section>
          <h2 class="dlr-text-heading">
            Cookies and Privacy Preferences Applicable to the Global Portal
          </h2>
          <p>
            We want to be transparent about the personal data we, and our
            partners, collect and how we use it so you can best exercise control
            over your personal data. The processing of your personal data in
            connection with cookies, and for other purposes, is governed by our
            <a
              href="https://www.digitalrealty.com/privacy"
              rel="noreferrer"
              target="_blank"
              >Privacy Practices</a
            >.
          </p>
        </section>

        <section>
          <h2 class="dlr-text-heading">What are cookies?</h2>
          <p>
            Cookies are small text files that may be stored on your computer or
            other device when you visit a website. The information does not
            usually directly identify you and is generally used to make websites
            work and to personalize your experience. Such cookies are set by us
            and are known as first party cookies. We also use third party
            cookies, which are cookies from a domain different from the domain
            of the website you are visiting, such as for advertising and
            marketing purposes. You can opt-in or opt-out out of cookies (except
            strictly necessary cookies) by clicking "Accept Cookies" or "Do Not
            Collect My Personal Information" on the cookie banner at startup or
            by clicking the Cookie settings button at the bottom of your screen
            to modify your selection.
          </p>
        </section>

        <section>
          <h2 class="dlr-text-heading">Cookies we use</h2>
          <p>
            All major internet browsers offer the option to manage the cookies
            that were installed on your computer or mobile device.
          </p>
          <p>
            If you do not wish for our website to place cookies on your computer
            or mobile device you may limit or delete these easily by adjusting
            your mobile or browser settings. In addition, you can set you mobile
            or browser settings in such a way that you get a notification every
            time you receive a cookie on your computer or mobile device, so that
            you can decide whether you wish to accept this cookie or not.
            Depending on your browser, you can set your browser to warn you
            before accepting cookies, or you can set it to refuse them. Please
            refer to the 'help' button (or similar) on your browser to learn
            more about how you can do this.
          </p>
          <p>
            Global Privacy Control (GPC) signals: When we detect GPC signals
            that express a user's preference for privacy, we will not set
            Targeting and Social Media Cookies. Strictly Necessary and
            Performance Cookies will still be set (see below). If you wish to
            turn off Performance Cookies you can do so by clicking the “Cookie
            Settings” button on the Portal navigation bar.
          </p>
          <p>
            Please note that when you disable certain cookies that enhance the
            functioning of our website, the possibility exists that certain
            parts of our website will no longer function properly and may impact
            your experience on our websites.
          </p>
          <p>
            If you use different devices to access our websites, you will need
            to ensure that each browser of each device is set to your cookie
            preference.
          </p>
        </section>

        <section>
          <h2 class="dlr-section-title">Cookie List</h2>
        </section>

        <section>
          <h2 class="dlr-text-heading">Strictly Necessary Cookies</h2>
          <p>
            These cookies are necessary for the website to function and cannot
            be switched off in our systems. They are usually only set in
            response to actions made by you which amount to a request for
            services, such as setting your privacy preferences, logging in or
            filling in forms. You can set your browser to block or alert you
            about these cookies, but some parts of the site will not then work.
            These cookies do not store any personally identifiable information.
          </p>
        </section>

        <section>${this.renderTable(cookieStrictlyNecessary)}</section>

        <section>
          <h2 class="dlr-text-heading">Performance Cookies</h2>
          <p>
            These cookies allow us to count visits and traffic sources so we can
            measure and improve the performance of our site. They help us to
            know which pages are the most and least popular and see how visitors
            move around the site. All information these cookies collect is
            aggregated and therefore anonymous. If you do not allow these
            cookies we will not know when you have visited our site, and will
            not be able to monitor its performance.
          </p>
        </section>

        <section>${this.renderTable(cookiePerformance)}</section>

        <section>
          <h2 class="dlr-text-heading">Targeting Cookies</h2>
          <p>
            These cookies may be set on our site by Digital Realty's Customer
            Success team to support and enhance customer support interactions.
            They may be used to identify your browser, device, and your account
            in order to remember your preferences, streamline communications and
            facilitate your interactions with our Global Portal. While these
            cookies do not store information that directly identifies you, they
            are based on unique identifiers linked to your browser or device. If
            you choose to disable these cookies, some customer support features
            may not function optimally.
          </p>
        </section>

        <section>${this.renderTable(cookieTargeting)}</section>

        <p>Last Updated: September 2025</p>
      </div>
    `;
  }
}

window.customElements.define('ix-cookie-policy', IxCookiePolicy);
